/**
 * Fuentes para la «muestra auténtica de listening».
 *
 * DECISIÓN A (aprobada 2026-08-27).
 *
 * En Baseline y checkpoints NO se usa una búsqueda de frase conocida en
 * YouGlish como prueba de comprensión auditiva general: si sabés de antemano
 * qué frase buscaste, la tarea deja de medir limpiamente cuánto entendés de un
 * fragmento de habla natural desconocido.
 *
 * YouGlish sigue siendo la herramienta para TARGETS CONCRETOS —palabra, frase,
 * reduction, connected speech, pronunciación, naturalidad— en el Día 2 y en el
 * Día 5 cuando corresponda. Son dos tareas distintas con dos herramientas
 * distintas.
 *
 * REGLA DE ROBUSTEZ: el curriculum nunca depende de una sola URL. Todo paso que
 * use estas fuentes sugiere varias y declara un `fallbackSinFuente`. Si mañana
 * una de estas páginas cambia o desaparece, el paso sigue siendo utilizable.
 *
 * Son enlaces y nada más: sin API, sin descarga, sin contenido protegido.
 */

import type { FuenteListening } from '../tipos.js';

export const FUENTES_LISTENING: FuenteListening[] = [
  {
    clave: 'npr',
    nombre: 'NPR',
    url: 'https://www.npr.org/',
    descripcion:
      'Radio pública estadounidense: noticias, entrevistas y reportajes cortos.',
    porQueSirve:
      'Inglés estadounidense a velocidad normal, con hablantes distintos y bastante habla no guionizada en las entrevistas. Muchas piezas traen transcripción, que sirve para comprobar DESPUÉS.',
    transcript: 'a-veces',
  },
  {
    clave: 'thisAmericanLife',
    nombre: 'This American Life',
    url: 'https://www.thisamericanlife.org/',
    descripcion: 'Programa narrativo estadounidense con entrevistas y relatos.',
    porQueSirve:
      'Mucha conversación real, con interrupciones, risas, dudas y habla espontánea: justo lo que no aparece en material didáctico. Publica transcripciones completas.',
    transcript: 'si',
  },
  {
    clave: 'pbsNewsHour',
    nombre: 'PBS NewsHour',
    url: 'https://www.pbs.org/newshour/',
    descripcion: 'Noticiero estadounidense en video, con segmentos de entrevista.',
    porQueSirve:
      'Video, lo que permite cortar 30–90 segundos con facilidad. Inglés estadounidense claro pero a velocidad real. Suele ofrecer transcripción.',
    transcript: 'a-veces',
  },
  {
    clave: 'youtubeCanalPropio',
    nombre: 'Un canal de YouTube que ya sigas',
    url: 'https://www.youtube.com/',
    descripcion:
      'Cualquier canal estadounidense que mires normalmente: entrevistas, podcasts en video, divulgación.',
    porQueSirve:
      'Es material que te interesa de verdad, así que no lo vas a abandonar en la semana tres. Los subtítulos automáticos sirven para comprobar después, aunque tienen errores: tratalos como referencia, no como verdad.',
    transcript: 'a-veces',
  },
  {
    clave: 'podcastPropio',
    nombre: 'Un podcast estadounidense que ya escuches',
    url: 'https://podcasts.google.com/',
    descripcion: 'Cualquier podcast conversacional en inglés estadounidense.',
    porQueSirve:
      'Habla espontánea sin apoyo visual, que es la condición más exigente y la más parecida a una llamada de trabajo. Muchos publican transcripción en su web.',
    transcript: 'a-veces',
  },
];

/**
 * Qué hacer si NINGUNA de las fuentes sugeridas funciona hoy.
 *
 * Esto es lo que garantiza que una URL rota nunca inutilice el paso.
 */
export const FALLBACK_SIN_FUENTE =
  'Si ninguna de las fuentes sugeridas te funciona hoy, no canceles el paso: usá cualquier video o podcast en inglés estadounidense que tengas a mano y que NO hayas escuchado antes. Lo único que importa es que el fragmento sea nuevo para vos, dure entre 30 y 90 segundos, y que no leas subtítulos en la primera escucha. La fuente es lo de menos; la condición de «desconocido y sin texto» es lo que hace válida la medición.';

export const FUENTE_LISTENING_POR_CLAVE = Object.fromEntries(
  FUENTES_LISTENING.map((f) => [f.clave, f]),
) as Record<string, FuenteListening>;
